import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useIntl } from 'umi';
import { notification } from 'tea-component';
import { getStudioBreakdown, updateStudioBreakdown } from '@sg/services/index';
import { round } from '@/pages/globalpedia/utils/common';
import { useExpandCollapse } from '@sg/hooks/useExpandCollapse';
import { createBtnPoint, SGBuriedBtnPoint, PortfolioOverviewTab } from '@/pages/strategic/utils/buriedPoint';
import { PhaseColums, EditPhaseEditColums } from '../pageConfig';
import { mapApiStudiosToCategoryChildren } from '../utils';
import { usePhaseTableAutoSave } from './usePhaseTableAutoSave';

interface IUsePhaseStudioTableOptions {
  activeCard: 'Revenue' | 'Profit';
  selectedMonth: string | null;
  onEditingChange?: (isEditing: boolean) => void;
}

export const usePhaseStudioTable = ({
  activeCard,
  selectedMonth,
  onEditingChange,
}: IUsePhaseStudioTableOptions) => {
  const { formatMessage } = useIntl();

  const tableRef = useRef<any>(null);
  const [expandedKeys, setExpandedKeys] = useState(['2']);
  const expandedKeysRef = useRef<string[]>(['2']);
  const isTrackingRef = useRef(false);

  const [isEditing, setIsEditing] = useState(false);
  // Studio Breakdown edit data (captured by onEditDataChange callback)
  // IMPORTANT: Must be declared before hasUnsavedChanges useMemo
  const [editData, setEditData] = useState<any[]>([]);
  // Studio Breakdown (Phase table) data state
  const [studioBreakdownData, setStudioBreakdownData] = useState<any[]>([]);
  const [studioBreakdownDataEndDate, setStudioBreakdownDataEndDate] = useState('');
  const [studioBreakdownLoading, setStudioBreakdownLoading] = useState(false);
  const [originalStudioBreakdownData, setOriginalStudioBreakdownData] = useState<any[]>([]);
  // Segment state for By Year / By Month toggle
  const [activeDateTypeSegment, setActiveDateTypeSegment] = useState('year');
  const [maxVsVallue, setMaxVsVallue] = useState(0);

  // Track if data has been modified during edit mode
  // IMPORTANT: Must be declared before hasUnsavedChanges useMemo
  const hasDataChangedRef = useRef(false);

  // Sync ref with state when state changes from other sources
  useEffect(() => {
    expandedKeysRef.current = expandedKeys;
  }, [expandedKeys]);

  // Helper function to find phase name by id in studioBreakdownData
  const findPhaseNameById = useCallback((id: string): string => {
    const findInData = (data: any[]): string | null => {
      for (const record of data) {
        if (String(record.id) === String(id)) {
          // Return Phase name, or Studio name if Phase is empty
          return record.Phase || record.Studio || 'Unknown';
        }
        if (record.children && Array.isArray(record.children)) {
          const found = findInData(record.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findInData(studioBreakdownData) || 'Unknown';
  }, [studioBreakdownData]);

  // Internal function to handle expanded keys change with tracking control
  const handleExpandedKeysChangeInternal = useCallback((keys: string[], shouldTrack = true) => {
    // Prevent double tracking
    if (isTrackingRef.current) {
      setExpandedKeys(keys);
      expandedKeysRef.current = keys;
      return;
    }

    // Use ref to get the previous keys to avoid stale closure issues
    const previousKeys = expandedKeysRef.current;
    const previousKeysSorted = [...previousKeys].sort().join(',');
    const keysSorted = [...keys].sort().join(',');
    const keysChanged = previousKeysSorted !== keysSorted;

    if (!keysChanged) {
      setExpandedKeys(keys);
      expandedKeysRef.current = keys;
      return;
    }

    // Set tracking flag to prevent double tracking
    isTrackingRef.current = true;

    // Only track buried points if shouldTrack is true (user manually clicked phase)
    if (shouldTrack) {
      // Find which phase was expanded or collapsed
      const newlyExpanded = keys.filter(key => !previousKeys.includes(key));
      const newlyCollapsed = previousKeys.filter(key => !keys.includes(key));

      // Track expansion
      newlyExpanded.forEach(key => {
        const phaseName = findPhaseNameById(key);
        const btnPoint = createBtnPoint(
          PortfolioOverviewTab,
          'phaseTableCollapse',
          `Phase Table Expand - ${phaseName}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      });

      // Track collapse
      newlyCollapsed.forEach(key => {
        const phaseName = findPhaseNameById(key);
        const btnPoint = createBtnPoint(
          PortfolioOverviewTab,
          'phaseTableCollapse',
          `Phase Table Collapse - ${phaseName}`,
        );
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      });
    }

    // Update the state and ref, then reset tracking flag
    setExpandedKeys(keys);
    expandedKeysRef.current = keys;

    // Reset tracking flag after a short delay to allow state update
    setTimeout(() => {
      isTrackingRef.current = false;
    }, 0);
  }, [findPhaseNameById]);

  // Wrapper for expandable addon - always tracks (user clicked phase)
  const handleExpandedKeysChange = useCallback((keys: string[]) => {
    handleExpandedKeysChangeInternal(keys, true);
  }, [handleExpandedKeysChangeInternal]);

  // 计算25F-25B的最大值
  const calculateMaxVsVallue = useCallback((data: any[]) => {
    let max = 0;
    const compareDiff = (entity: any) => {
      const diff = Math.abs((entity?.['25F'] || 0) - (entity?.['25B'] || 0));
      if (diff > max) {
        max = diff;
      }
    };

    data.forEach((item: any) => {
      compareDiff(item);
      if (Array.isArray(item?.children)) {
        item.children.forEach((child: any) => compareDiff(child));
      }
    });

    setMaxVsVallue(max);
    return data;
  }, []);

  // 展开/折叠所有有 children 的目录 (must be defined before usePhaseTableAutoSave hook)
  const { isAllExpanded, toggleExpandAll, expandAllCategoriesWithChildren } = useExpandCollapse({
    expandedKeys,
    setExpandedKeys,
    expandedKeysRef,
    data: studioBreakdownData,
  });

  // Auto-save for Phase Table
  const {
    autoSaveVisible,
    handlePhaseEdit,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    clearPhaseAutoSave,
  } = usePhaseTableAutoSave(
    isEditing,
    studioBreakdownData,
    editData,
    activeCard,
    tableRef,
    setStudioBreakdownData,
    expandAllCategoriesWithChildren,
  );

  // Memoize unsaved changes check for RoutePrompt to prevent unnecessary re-evaluations
  const hasUnsavedChanges = useMemo(() => {
    if (!isEditing) return false;

    // Check if user has manually changed data
    // This flag is set by onEditDataChange
    const hasManualChanges = hasDataChangedRef.current;

    // Compare current edit data with original data
    const hasDataChanges = editData.length > 0
      && JSON.stringify(editData) !== JSON.stringify(originalStudioBreakdownData);

    return hasManualChanges && hasDataChanges;
  }, [isEditing, editData, originalStudioBreakdownData]);

  // Fetch studio breakdown data (Phase table)
  const fetchStudioBreakdownData = useCallback(async () => {
    try {
      setStudioBreakdownLoading(true);
      // Pass type parameter based on activeCard: 'Revenue' -> 'revenue', 'Profit' -> 'profit'
      const type = activeCard === 'Revenue' ? 'revenue' : 'profit';

      // Build payload with type and optional datatime parameter (already formatted as "YYYY-MM")
      const payload: { type: string; datatime?: string; view_mode: string } = {
        type,
        view_mode: activeDateTypeSegment, // 'year' or 'month'
      };
      if (selectedMonth) {
        payload.datatime = selectedMonth;
      }

      const response = await getStudioBreakdown(payload);
      const apiData = response?.data;
      if (!apiData) {
        setStudioBreakdownData([]);
        setStudioBreakdownDataEndDate('');
        return;
      }

      // Transform API data to match table format
      const transformedData: any[] = [];

      // Add "All Total" row first
      if (apiData.all_total && apiData.all_total.value) {
        const totalData = apiData.all_total.value;
        transformedData.push({
          id: 'all-total',
          Phase: 'All Total',
          Studio: '',
          '25A': totalData.actual !== null && totalData.actual !== undefined ? round(totalData.actual, 2) : null,
          '25F': totalData.forecast !== null && totalData.forecast !== undefined ? round(totalData.forecast, 2) : null,
          '25B': totalData.kpi !== null && totalData.kpi !== undefined ? round(totalData.kpi, 2) : null,
          'Opportunities & Risks': totalData.opportunities || '',
          'Action & Note': totalData.action_note || '',
        });
      }

      // Process categories (functional, etc.)
      if (apiData.categories && Array.isArray(apiData.categories)) {
        apiData.categories.forEach((category: any) => {
          // Add category total row
          const categoryTotalData = category.total_row?.value || {};
          const categoryRow: any = {
            id: `category_${category.category_name}`, // Prefix with 'category_' to avoid collisions with studio IDs
            Phase: category.category_name,
            Studio: '',
            '25A': categoryTotalData.actual !== null && categoryTotalData.actual !== undefined ? round(categoryTotalData.actual, 2) : null,
            '25F': categoryTotalData.forecast !== null && categoryTotalData.forecast !== undefined ? round(categoryTotalData.forecast, 2) : null,
            '25B': categoryTotalData.kpi !== null && categoryTotalData.kpi !== undefined ? round(categoryTotalData.kpi, 2) : null,
            'Opportunities & Risks': categoryTotalData.opportunities || '',
            'Action & Note': categoryTotalData.action_note || '',
          };

          const studioChildren = mapApiStudiosToCategoryChildren(
            category.studios,
            category.category_name,
          );
          if (studioChildren.length > 0) {
            categoryRow.children = studioChildren;
          }

          transformedData.push(categoryRow);
        });
      }

      calculateMaxVsVallue(transformedData);
      setStudioBreakdownData(transformedData);
      setStudioBreakdownDataEndDate(apiData.datatime);
      // 自动展开第一个有 studio 子行的类别
      const firstExpandableCategory = transformedData.find(
        (row: any) => Array.isArray(row.children) && row.children.length > 0,
      );
      if (firstExpandableCategory) {
        setExpandedKeys([String(firstExpandableCategory.id)]);
      } else {
        setExpandedKeys([]);
      }
      setOriginalStudioBreakdownData(JSON.parse(JSON.stringify(transformedData))); // 深拷贝保存转换后的数据用于对比
    } catch (error) {
      console.error('Error fetching studio breakdown data:', error);
      notification.error({
        description: 'Failed to load breakdown data',
      });
      setStudioBreakdownData([]);
      setStudioBreakdownDataEndDate('');
      setOriginalStudioBreakdownData([]);
    } finally {
      setStudioBreakdownLoading(false);
    }
  }, [activeCard, selectedMonth, activeDateTypeSegment, calculateMaxVsVallue]);

  // Refetch breakdown data when activeCard or segment changes (Revenue/Profit)
  useEffect(() => {
    fetchStudioBreakdownData();
  }, [fetchStudioBreakdownData]);

  const editStudioBreakdownData = useCallback(async (value: any) => {
    try {
      setStudioBreakdownLoading(true);
      const dataType = activeCard === 'Revenue' ? 'revenue' : 'profit';
      const apiData: any = {
        type: dataType,
        // Date picker wins when set; otherwise use datatime from getStudioBreakdown.
        datatime: selectedMonth || studioBreakdownDataEndDate,
        view_mode: activeDateTypeSegment, // 'year' or 'month'
        // categories_to_create: [],
        // categories_to_update: [],
        // category_orders: [],
        // studios_to_move: [],
        // studio_orders: [],
        studio_notes: [],
        category_notes: [],
      };

      // 构建原始数据的 Map（用于快速查找）
      const originalCategoriesMap = new Map();
      const originalStudiosMap = new Map();

      originalStudioBreakdownData.forEach((category: any) => {
        originalCategoriesMap.set(category.id, category);
        if (category.children && Array.isArray(category.children)) {
          category.children.forEach((studio: any) => {
            originalStudiosMap.set(studio.id, studio);
          });
        }
      });

      // 检查 category notes 和 studio notes 变动
      value.forEach((editedCategory: any) => {
        const originalCategory = originalCategoriesMap.get(editedCategory.id);

        if (originalCategory) {
          // 检查 Opportunities & Risks 或 Action & Note 是否变化
          const riskChanged = originalCategory['Opportunities & Risks'] !== editedCategory['Opportunities & Risks'];
          const noteChanged = originalCategory['Action & Note'] !== editedCategory['Action & Note'];

          if (riskChanged || noteChanged) {
            apiData.category_notes.push({
              category_name: editedCategory.Phase, // 使用编辑后的名字
              opportunities_risks: editedCategory['Opportunities & Risks'] || '',
              action_note: editedCategory['Action & Note'] || '',
            });
          }
        } else {
          // 新增的 category，检查是否有 notes 内容
          const hasRiskContent = editedCategory['Opportunities & Risks'] && editedCategory['Opportunities & Risks'].trim() !== '';
          const hasNoteContent = editedCategory['Action & Note'] && editedCategory['Action & Note'].trim() !== '';

          if (hasRiskContent || hasNoteContent) {
            apiData.category_notes.push({
              category_name: editedCategory.Phase,
              opportunities_risks: editedCategory['Opportunities & Risks'] || '',
              action_note: editedCategory['Action & Note'] || '',
            });
          }
        }
        if (editedCategory.id === 'all-total') return;

        // 检查 studios 的 notes 变动（含嵌套 children）
        if (editedCategory.children && Array.isArray(editedCategory.children)) {
          editedCategory.children.forEach((editedStudio: any) => {
            const originalStudio = originalStudiosMap.get(editedStudio.id);
            if (!originalStudio) {
              return;
            }

            // 检查 studio notes 变化
            const riskChanged = originalStudio['Opportunities & Risks'] !== editedStudio['Opportunities & Risks'];
            const noteChanged = originalStudio['Action & Note'] !== editedStudio['Action & Note'];
            if (!riskChanged && !noteChanged) {
              return;
            }

            // Group parent (no studio_id, has children) → category_notes, studio_name as category_name
            if (!editedStudio.studio_id && editedStudio.isStudioGroup) {
              apiData.category_notes.push({
                category_name: editedStudio.Studio,
                opportunities_risks: editedStudio['Opportunities & Risks'] || '',
                action_note: editedStudio['Action & Note'] || '',
              });
              return;
            }

            if (!editedStudio.studio_id) {
              return;
            }

            apiData.studio_notes.push({
              studio_id: editedStudio.studio_id,
              opportunities_risks: editedStudio['Opportunities & Risks'] || '',
              action_note: editedStudio['Action & Note'] || '',
            });
          });
        }
      });

      // Notes-only edit: skip API when nothing changed
      if (apiData.category_notes.length === 0 && apiData.studio_notes.length === 0) {
        clearPhaseAutoSave();
        hasDataChangedRef.current = false;
        setStudioBreakdownLoading(false);
        return;
      }

      // 调用 API 更新数据
      await updateStudioBreakdown(apiData);

      // Show success notification
      notification.success({
        description: 'Operate Successfully',
      });

      // Clear auto-save data on successful save
      clearPhaseAutoSave();

      // Reset the data changed flag since we saved successfully
      hasDataChangedRef.current = false;

      // 重新获取表单数据
      await fetchStudioBreakdownData();
      setStudioBreakdownLoading(false);
    } catch (error) {
      console.error('Error transforming studio breakdown data:', error);
      notification.error({
        description: 'Save failed, please try again',
      });
      setStudioBreakdownLoading(false);
      throw error;
    }
  }, [
    activeCard,
    selectedMonth,
    studioBreakdownDataEndDate,
    activeDateTypeSegment,
    originalStudioBreakdownData,
    clearPhaseAutoSave,
    fetchStudioBreakdownData,
  ]);

  const handleEditingChange = useCallback((editing: boolean) => {
    setIsEditing(editing);
    onEditingChange?.(editing);

    // 进入编辑模式时，展开所有有 children 的目录
    if (editing) {
      const currentData = tableRef.current?.getTableData?.() || [];
      expandAllCategoriesWithChildren(currentData);
      // Reset the flag when entering edit mode
      hasDataChangedRef.current = false;
    } else {
      // Clear auto-save when user cancels/exits edit mode
      clearPhaseAutoSave();
    }
  }, [onEditingChange, expandAllCategoriesWithChildren, clearPhaseAutoSave]);

  // Stable column configs — avoid recreating render fns on every parent re-render (e.g. onEditDataChange).
  const phaseColumns = useMemo(
    () => PhaseColums(
      maxVsVallue,
      studioBreakdownDataEndDate,
      formatMessage,
      isAllExpanded,
      toggleExpandAll,
      activeDateTypeSegment,
    ),
    [maxVsVallue, studioBreakdownDataEndDate, formatMessage, isAllExpanded, toggleExpandAll, activeDateTypeSegment],
  );

  const phaseEditColumns = useMemo(
    () => EditPhaseEditColums(
      maxVsVallue,
      studioBreakdownDataEndDate,
      formatMessage,
      activeDateTypeSegment,
    ),
    [maxVsVallue, studioBreakdownDataEndDate, formatMessage, activeDateTypeSegment],
  );

  const handleEditDataChange = useCallback((data: any) => {
    // Mark that data has been changed
    hasDataChangedRef.current = true;
    setEditData(data);
  }, []);

  const handleEditClick = useCallback(() => {
    // Track buried point
    const btnPoint = createBtnPoint(
      PortfolioOverviewTab,
      'phaseTableEdit',
      'Phase Table Edit',
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }

    // Check for auto-save before entering edit mode
    handlePhaseEdit();
    // 展开所有二级页面（除了第一个 all-total）
    // Pass false to shouldTrack to prevent buried point tracking when expanding via Edit button
    if (studioBreakdownData.length > 1) {
      const allKeys = studioBreakdownData
        .slice(1) // 跳过第一个 all-total
        .map((item: any) => String(item.id));
      handleExpandedKeysChangeInternal(allKeys, false);
    }
  }, [handlePhaseEdit, studioBreakdownData, handleExpandedKeysChangeInternal]);

  const handleCancelClick = useCallback(() => {
    // Clear auto-save on cancel
    clearPhaseAutoSave();
    tableRef.current?.handleCancel();
    // 取消编辑后恢复到只展开第一个类别（跳过 all-total）
    if (studioBreakdownData.length > 1) {
      setExpandedKeys([String(studioBreakdownData[1].id)]);
    }
  }, [clearPhaseAutoSave, studioBreakdownData]);

  const handleSegmentChange = useCallback((value: string) => {
    // Track buried point for segment change
    if (value === 'month') {
      const btnPoint = createBtnPoint(PortfolioOverviewTab, 'byMonth', 'By Month');
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    } else if (value === 'year') {
      const btnPoint = createBtnPoint(PortfolioOverviewTab, 'byYear', 'By Year');
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    }
    setActiveDateTypeSegment(value);
  }, []);

  return {
    tableRef,
    isEditing,
    studioBreakdownData,
    studioBreakdownLoading,
    originalStudioBreakdownData,
    activeDateTypeSegment,
    expandedKeys,
    handleExpandedKeysChange,
    phaseColumns,
    phaseEditColumns,
    fetchStudioBreakdownData,
    editStudioBreakdownData,
    calculateMaxVsVallue,
    handleEditingChange,
    handleEditDataChange,
    hasUnsavedChanges,
    autoSaveVisible,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    handleEditClick,
    handleCancelClick,
    handleSegmentChange,
    clearPhaseAutoSave,
  };
};
